import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../../components/PageHero";
import FilterBand from "../../../../components/FilterBand";
import MetricCard from "../../../../components/MetricCard";
import MetricCardGroup from "../../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../../components/ShellCard";
import TablePagination from "../../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { listarBids } from "services/bidFreteApi";
import NovoBidWizard from "./NovoBidWizard";
import BidDetalhe from "./BidDetalhe";
import { ETAPA_UI, ETAPA_STATUS_UI, PRESET_UI, PRESETS_ORDEM, SITUACAO_UI, SITUACOES_ORDEM, erroDaApi, etapaAtualDe, fmtBRL, fmtSiglas, statusDaEtapa, fmtDataHora, fmtNumero, fmtPeriodo } from "./regras";
import "../Leiloes/styles.css";
import "./styles.css";

// GoBID › Operação › BID de Frete (H2 PO1-2841, épico PO1-2413). Molde: a tela
// de Leilões (PageHero, FilterBand, MetricCardGroup, TableSurface) com a
// paginação no servidor da Conferência de Faturas. O detalhe abre por
// ?abrir=<id> (modal), nunca por rota com parâmetro.

const PAGE_SIZES = [10, 20, 50, 100];

const CARDS = [
  { key: "RASCUNHO", label: "Rascunhos", campo: "rascunho", icon: "tim-icons icon-pencil", tone: "neutral", helper: "escopo salvo, sem convite" },
  { key: "PUBLICADO", label: "Publicados", campo: "publicado", icon: "tim-icons icon-send", tone: "info", helper: "convites enviados (RFP)" },
  { key: "ENCERRADO", label: "Encerrados", campo: "encerrado", icon: "tim-icons icon-check-2", tone: "success", helper: "integração concluída" },
  { key: "CANCELADO", label: "Cancelados", campo: "cancelado", icon: "tim-icons icon-simple-remove", tone: "danger", helper: "descartados" },
];

function Badge({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone || "neutral"}`} title={title}>
      {children}
    </span>
  );
}

const Bids = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const history = useHistory();
  const empresaId = empresa && empresa.value;

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [situacaoFiltro, setSituacaoFiltro] = useState(null);
  const [presetFiltro, setPresetFiltro] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detalheId, setDetalheId] = useState(null);
  const [novoAberto, setNovoAberto] = useState(false);

  // Deep-link lido uma vez no mount (padrão dos Leilões).
  const abrirRef = useRef(new URLSearchParams(history.location.search).get("abrir"));

  const carregar = useCallback(async () => {
    if (!empresaId || !hasEmpresaSelection) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listarBids(empresaId, { situacao: situacaoFiltro, preset: presetFiltro, busca: buscaAplicada, page, pageSize });
      setDados(r);
    } catch (requestError) {
      const msg = erroDaApi(requestError, "Erro ao carregar os BIDs.").mensagem;
      setError(msg);
      addToast({ place: "tr", type: "danger", title: "BID de Frete", description: msg, icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setLoading(false);
    }
  }, [empresaId, hasEmpresaSelection, situacaoFiltro, presetFiltro, buscaAplicada, page, pageSize, addToast]);

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setDados(null);
      return;
    }
    carregar();
  }, [hasEmpresaSelection, carregar]);

  useEffect(() => {
    const abrir = abrirRef.current;
    if (!empresaId || !abrir) return;
    abrirRef.current = null;
    setDetalheId(abrir);
    history.replace({ pathname: history.location.pathname, search: "" });
  }, [empresaId, history]);

  const itens = (dados && (dados.items || dados.itens)) || [];
  const total = (dados && (dados.totalCount != null ? dados.totalCount : dados.total)) || 0;
  const cards = (dados && dados.cards) || {};

  if (!hasEmpresaSelection) {
    return (
      <div className="content ff-page">
        <SpecificEmpresaRequiredNotice title="Selecione um embarcador no cabeçalho" message="Esta tela usa o embarcador selecionado no topo para listar os BIDs de frete." />
      </div>
    );
  }

  const toggleSituacao = (key) => {
    setSituacaoFiltro((cur) => (cur === key ? null : key));
    setPage(1);
  };

  return (
    <div className="content ff-page">
      <PageHero
        eyebrow="GoBID · Operação"
        title="BID de Frete"
        description="Recalcula as NFs reais de um período com todas as transportadoras candidatas e compara com o frete cobrado. Benchmark usa só as suas tabelas; o RFP convida transportadoras, recebe propostas e ativa as vencedoras."
        actions={
          <>
            <Button color="primary" className="btn-sm" onClick={() => setNovoAberto(true)}>
              + Novo BID
            </Button>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar"
        searchPlaceholder="Nome ou nº do BID..."
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={(v) => {
          setBuscaAplicada(v === undefined ? searchTerm : v);
          setPage(1);
        }}
        activeFiltersCount={(situacaoFiltro ? 1 : 0) + (presetFiltro ? 1 : 0)}
        open={mostrarFiltros}
        onToggle={setMostrarFiltros}
        panelTitle="Filtros"
        busy={loading}
        onApply={() => {
          setBuscaAplicada(searchTerm);
          setPage(1);
          setMostrarFiltros(false);
        }}
        onClear={() => {
          setSituacaoFiltro(null);
          setPresetFiltro("");
          setSearchTerm("");
          setBuscaAplicada("");
          setPage(1);
        }}
      >
        <Row className="ff-filters-row">
          <Col md="4">
            <FormGroup>
              <Label>Situação</Label>
              <Input type="select" value={situacaoFiltro || ""} onChange={(e) => toggleSituacao(e.target.value || null)}>
                <option value="">Todas</option>
                {SITUACOES_ORDEM.map((k) => (
                  <option key={k} value={k}>
                    {SITUACAO_UI[k].label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Modelo</Label>
              <Input
                type="select"
                value={presetFiltro}
                onChange={(e) => {
                  const v = e.target.value;
                  setPresetFiltro(v);
                  setPage(1);
                }}
              >
                <option value="">Todos</option>
                {PRESETS_ORDEM.map((k) => (
                  <option key={k} value={k}>
                    {PRESET_UI[k].label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup title="Situação" description="Clique num card para filtrar a lista; clique de novo para desfazer.">
        {CARDS.map((c) => (
          <MetricCard key={c.key} label={c.label} value={String(cards[c.campo] || 0)} helper={c.helper} iconClassName={c.icon} tone={c.tone} active={situacaoFiltro === c.key} onClick={() => toggleSituacao(c.key)} />
        ))}
      </MetricCardGroup>

      <ShellCard title="BIDs" description={`${total} resultado(s)`}>
        <TableSurface>
          {error ? (
            <div className="lf-empty-state">{error}</div>
          ) : loading && !dados ? (
            <div className="lf-empty-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Carregando BIDs...
            </div>
          ) : itens.length === 0 ? (
            <div className="lf-empty-state">Nenhum BID para os filtros informados.</div>
          ) : (
            <Table className="ff-table bf-table hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>Nº</th>
                  <th>Nome</th>
                  <th>Modelo</th>
                  <th>Período</th>
                  <th>Destino</th>
                  <th>Etapa atual</th>
                  <th className="is-number">NFs</th>
                  <th className="is-number">Cobrado</th>
                  <th>Situação</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => {
                  const sit = SITUACAO_UI[item.situacao] || SITUACAO_UI.RASCUNHO;
                  // A lista do backend não traz as etapas (só o GET /:id); sem elas, mostra "—".
                  const etapa = etapaAtualDe(item);
                  const etapaStatus = item.etapaAtual && item.etapaAtual.status ? item.etapaAtual.status : etapa ? statusDaEtapa(item, etapa) : null;
                  const ui = ETAPA_UI[etapa];
                  const st = ETAPA_STATUS_UI[etapaStatus];
                  return (
                    <tr key={item.id} className="is-clicavel" onClick={() => setDetalheId(item.id)}>
                      <td>#{item.id}</td>
                      <td>
                        <strong>{item.nome}</strong>
                        <div className="lf-muted">criado em {fmtDataHora(item.criadoEm || item.creationTime)}</div>
                      </td>
                      <td>{PRESET_UI[item.preset] ? PRESET_UI[item.preset].label : item.preset || "—"}</td>
                      <td>{fmtPeriodo(item.dataEmissaoDe, item.dataEmissaoAte)}</td>
                      <td>{item.ufs ? fmtSiglas(item.ufs) : fmtSiglas(item.regioes)}</td>
                      <td>
                        {ui ? ui.label : "—"}
                        {st ? <div className={`lf-muted bf-etapa-status tone-${st.tone}`}>{st.label}</div> : null}
                      </td>
                      <td className="is-number">
                        {fmtNumero(item.qtdNf)}
                        {item.qtdNfComparavel != null ? <div className="lf-muted">{fmtNumero(item.qtdNfComparavel)} c/ CTe</div> : null}
                      </td>
                      <td className="is-number">{item.vrBaselineTotal != null ? fmtBRL(Number(item.vrBaselineTotal)) : "—"}</td>
                      <td>
                        <Badge tone={sit.tone}>{sit.label}</Badge>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="lf-btn-abrir"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetalheId(item.id);
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {!error && total > 0 ? (
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              pageSizes={PAGE_SIZES}
              itemLabel="BID(s)"
              disabled={loading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          ) : null}
        </TableSurface>
      </ShellCard>

      <NovoBidWizard
        isOpen={novoAberto}
        onClose={() => {
          setNovoAberto(false);
          carregar(); // o RFP pode ter gravado um rascunho antes de fechar
        }}
        empresaId={empresaId}
        onCriado={(criado) => {
          setNovoAberto(false);
          addToast({ place: "tr", type: "success", title: `BID #${criado.id} salvo`, description: "Escopo salvo. Crie a baseline para congelar as NFs.", icon: "tim-icons icon-check-2" });
          if (criado.avisoConvidados) addToast({ place: "tr", type: "warning", title: "Convidadas", description: `${criado.avisoConvidados} Inclua pela aba Convites.`, icon: "tim-icons icon-alert-circle-exc" });
          carregar();
          if (criado && criado.id) setDetalheId(criado.id);
        }}
      />

      <BidDetalhe
        isOpen={Boolean(detalheId)}
        onClose={() => {
          setDetalheId(null);
          carregar();
        }}
        empresaId={empresaId}
        bidId={detalheId}
        onChanged={() => carregar()}
      />
    </div>
  );
};

export default Bids;
